import type { Dispatch, MutableRefObject, SetStateAction } from "react";
import type { Account, ExpenseInput, IOU, Owner, SavingGoal, TrackedItemInput } from "../../types";
import { advanceDueDate, money } from "../../lib/finance";
import ExpenseLoanFields, { type ExpenseLoanFieldsProps } from "./ExpenseLoanFields";
import EntrySplitFields from "./EntrySplitFields";
import TrackedItemsEditor from "../../components/TrackedItemsEditor";
import EntryGoalFields from "./EntryGoalFields";
import type { useRecurringEntry } from "./use-recurring-entry";

// Everything an entry can carry but usually does not: pending status, household splitting, loans,
// household share overrides, foreign-currency markup, recurring schedule, time/goals, and tracked prices.

export interface ExpenseOptionalDetailsProps {
  input: ExpenseInput;
  setInput: Dispatch<SetStateAction<ExpenseInput>>;
  account: Account | undefined;
  entryType: "Expense" | "Income" | "Transfer";
  goals?: SavingGoal[];
  busy: boolean;
  /** A save running elsewhere, or a half-finished edit being retried: both freeze the form. */
  externalBusy: boolean;
  freezeFields: boolean;
  locked: boolean;
  isIOU: boolean;
  loanTransaction?: boolean;
  clearing?: boolean;
  canTrackIOU: boolean;
  /** Price tracking belongs to expenses; income and transfers never offer it. */
  allowTracking: boolean;
  profileNames: Owner[];
  payerProfile: Owner;
  field: (key: keyof ExpenseInput, value: string) => void;
  changeMarkup: (value: string) => void;
  trackMarkup: boolean;
  setTrackMarkup: (value: boolean) => void;
  markupAutoCalc: boolean;
  setMarkupAutoCalc: (value: boolean) => void;
  /** The amount the markup was last worked out against, so it can be recalculated when that moves. */
  markupBaseline: MutableRefObject<string>;
  trackedItems: TrackedItemInput[];
  setTrackedItems: Dispatch<SetStateAction<TrackedItemInput[]>>;
  loan: Omit<ExpenseLoanFieldsProps, "input" | "account" | "isIOU"> & { openIouMatches: IOU[] };
  recurring: ReturnType<typeof useRecurringEntry>["fields"];
}

export default function ExpenseOptionalDetails(props: ExpenseOptionalDetailsProps) {
  const {
    input, setInput, account, entryType, goals, busy, externalBusy, freezeFields, locked,
    isIOU, loanTransaction, clearing, canTrackIOU, allowTracking, profileNames, payerProfile,
    field, changeMarkup, trackMarkup, setTrackMarkup, markupAutoCalc, setMarkupAutoCalc,
    markupBaseline, trackedItems, setTrackedItems, recurring,
  } = props;
  const loanProps = { ...props.loan, input, account, isIOU };
  const showGoalField = entryType === "Expense" && !isIOU && !!goals?.length;

  return (
    <div className="optional-details">
      {entryType === "Expense" && !isIOU && !loanTransaction && !clearing && (
        <section className="household-share">
          <label className="household-share-toggle">
            <input
              type="checkbox"
              checked={!!input.pending}
              onChange={(e) => setInput((v) => ({ ...v, pending: e.target.checked }))}
            />
            Pending at the bank
          </label>
          <small>
            {input.pending
              ? "Still counts in your balance. Mark it cleared once the bank settles it. The month cannot be reconciled until then."
              : "Mark as unsettled if the bank might adjust the final amount or FX rate."}
          </small>
        </section>
      )}
      {entryType === "Expense" && !isIOU && !loanTransaction && !clearing && (
        <EntrySplitFields
          input={input}
          setInput={setInput}
          profiles={profileNames}
          payerProfile={payerProfile}
          currency={account?.currency || "MVR"}
        />
      )}
      {entryType !== "Transfer" && !isIOU && !input.allocations && (
        <section className="household-share">
          <label className="household-share-toggle">
            <input
              type="checkbox"
              checked={input.reportableAmount !== undefined}
              onChange={(e) =>
                setInput((v) => ({
                  ...v,
                  reportableAmount: e.target.checked ? (v.amount || "0") : undefined,
                }))
              }
            />
            Set household share
          </label>
          {input.reportableAmount !== undefined ? (
            <div className="optional-subfields">
              <label>
                Amount counted in household totals ({account?.currency})
                <input
                  aria-label="Household share"
                  inputMode="decimal"
                  required
                  pattern="[0-9]+([.][0-9]{1,2})?"
                  value={input.reportableAmount}
                  onChange={(e) => setInput((v) => ({ ...v, reportableAmount: e.target.value }))}
                />
                <small>
                  Use 0 for reimbursements or money belonging to someone else. The account still moves by the full amount.
                </small>
              </label>
            </div>
          ) : (
            <small>
              The full amount counts as household {entryType === "Income" ? "income" : "spending"}.
            </small>
          )}
        </section>
      )}
      {entryType === "Expense" && !isIOU && (
        <section className="household-share">
          <label className="household-share-toggle">
            <input
              type="checkbox"
              checked={trackMarkup}
              onChange={(e) => {
                setTrackMarkup(e.target.checked);
                setMarkupAutoCalc(false);
                if (!e.target.checked) setInput((v) => ({ ...v, markup: "" }));
                else markupBaseline.current = input.amount;
              }}
            />
            Bank added a markup
          </label>
          {trackMarkup ? (
            <div className="optional-subfields">
              <label>
                Markup <small>%</small>
                <input
                  aria-label="Markup percentage"
                  inputMode="decimal"
                  pattern="[0-9]+([.][0-9]{1,2})?"
                  value={input.markup || ""}
                  onChange={(e) => changeMarkup(e.target.value)}
                  placeholder="0"
                />
              </label>
              <label className="household-share-toggle">
                <input
                  type="checkbox"
                  checked={markupAutoCalc}
                  onChange={(e) => {
                    markupBaseline.current = input.amount;
                    setMarkupAutoCalc(e.target.checked);
                  }}
                />
                Auto-calculate the other field
              </label>
              <small>
                {markupAutoCalc
                  ? `Editing amount or markup updates the other, based on ${money(Number(markupBaseline.current) || 0, account?.currency)} before the markup.`
                  : "Enter the amount and markup independently."}
              </small>
            </div>
          ) : (
            <small>Record a card or bank foreign-exchange fee percentage.</small>
          )}
        </section>
      )}
      {entryType !== "Transfer" && !input.allocations && canTrackIOU && (
        <ExpenseLoanFields {...loanProps} />
      )}
      {entryType !== "Transfer" && !isIOU && !input.allocations && recurring.canMakeRecurring && (
        <section className="household-share">
          <label className="household-share-toggle">
            <input
              type="checkbox"
              checked={recurring.makeRecurring}
              onChange={(e) => recurring.setMakeRecurring(e.target.checked)}
            />
            {entryType === "Income" ? "Make this recurring income" : "Make this a recurring bill"}
          </label>
          {recurring.makeRecurring ? (
            <div className="optional-subfields">
              <div className="optional-grid-2">
                <label>
                  Frequency
                  <select
                    aria-label="Recurring frequency"
                    value={recurring.frequency}
                    onChange={(e) =>
                      recurring.setFrequency(e.target.value as "weekly" | "monthly" | "yearly")
                    }
                  >
                    <option value="weekly">Weekly</option>
                    <option value="monthly">Monthly</option>
                    <option value="yearly">Yearly</option>
                  </select>
                </label>
                <label>
                  <span className="field-label">Remind me <small>Days before</small></span>
                  <input
                    aria-label="Remind me days before"
                    inputMode="numeric"
                    pattern="[0-9]+"
                    value={recurring.reminderDaysBefore}
                    onChange={(e) => recurring.setReminderDaysBefore(e.target.value)}
                    placeholder="e.g. 3"
                  />
                </label>
              </div>
              <small>
                This entry becomes the first {entryType === "Income" ? "received" : "paid"} cycle. Next{" "}
                {entryType === "Income" ? "expected" : "due"}{" "}
                {advanceDueDate(input.date, recurring.frequency)}.
              </small>
            </div>
          ) : (
            <small>Schedule future reminders and due dates automatically.</small>
          )}
        </section>
      )}
      <div className={showGoalField ? "optional-meta-grid optional-grid-2" : "optional-meta-grid"}>
        <label>
          Time
          <input
            type="time"
            aria-label="Time"
            value={input.time || ""}
            onChange={(e) => field("time", e.target.value)}
          />
        </label>
        {showGoalField && (
          <EntryGoalFields input={input} account={account} goals={goals || []}
            onChange={values => setInput(current => ({ ...current, ...values }))} />
        )}
      </div>
      {allowTracking && entryType === "Expense" && !isIOU && (
        <TrackedItemsEditor
          items={trackedItems}
          onChange={setTrackedItems}
          currency={account?.currency || "MVR"}
          disabled={locked || busy || externalBusy || freezeFields}
        />
      )}
    </div>
  );
}
